var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useCompanyStore } from "../../../../application/stores/company.store";
import { useFormValidationStore } from "../../../../application/stores/formValidation.store";
import { getQuestionnaireByServiceIdAndType } from "../../../../application/usecases/getQuestionnaire";
import Loader from "../../../../components/common/Loaders/Loader";
import { StepperNavigation } from "../../../../components/common/Stepper/StepperNavigation";
import { FormStatus } from "../../../../contexts/FormProgressContext";
import { useFormTranslator } from "../../../../hooks/useFormTranslations";
import { ServiceFormFillingRepository } from "../../../../infrastructure/repositories/serviceFormFilling.impl.repository";
import { AnswerProcessorService, FormEvaluatorService, PDDAnswerRulesService, ServiceFormFillingService, } from "../../../../infrastructure/services";
import { DocumentStatus } from "../../../../types/DocumentationTypes";
import { scrollToTop } from "../../../../utils/common-utils";
import { SectionNavigator } from "../CommonComponents";
import { ModalExitForm } from "./Modals/ModalExitForm";
import { ModalFinishForm } from "./Modals/ModalFinishForm";
import { ModalInitForm } from "./Modals/ModalInitForm";
import { SectionRenderer } from "./SectionRenderer";
export const PDDForm = ({ isReadMode = false }) => {
    const { t } = useTranslation();
    const translator = useFormTranslator();
    const navigate = useNavigate();
    const processor = new AnswerProcessorService();
    const answerRules = new PDDAnswerRulesService();
    const evaluator = new FormEvaluatorService();
    const serviceFormFillingService = new ServiceFormFillingService();
    const serviceFormFillingRepository = new ServiceFormFillingRepository();
    const { company, setFormProgressStatus } = useCompanyStore();
    const [formState, setFormState] = useState(null);
    const [answers, setAnswers] = useState({});
    const [activeSectionId, setActiveSectionId] = useState(null);
    const [serviceFormFillingId, setServiceFormFillingId] = useState(null);
    const [formVersion, setFormVersion] = useState("");
    const [openDialogInitInfo, setOpenDialogInitInfo] = useState(false);
    const [openDialogExitForm, setOpenDialogExitForm] = useState(false);
    const [openDialogFinishForm, setOpenDialogFinishForm] = useState(false);
    const [isDpdMandatory, setIsDpdMandatory] = useState(false);
    // store de validación
    const { validateField, clearError, isSectionValid: checkSectionValid } = useFormValidationStore();
    const context = useMemo(() => ({
        activityId: company.activityId,
        employees: company.employees,
        isDpdMandatory,
    }), [company.activityId, company.employees, isDpdMandatory]);
    // ──────────────────────────────────────────────────────────────────────────────
    // 1) CARGA INICIAL: Solo UNA vez, cuando cambien company.activityId o company.employees
    // ──────────────────────────────────────────────────────────────────────────────
    useEffect(() => {
        let didCancel = false;
        (() => __awaiter(void 0, void 0, void 0, function* () {
            const { form, answers: prevAnswers, serviceFormFillingId, version } = yield getQuestionnaireByServiceIdAndType(1, company, "PDD");
            if (didCancel)
                return;
            setFormState(form);
            setServiceFormFillingId(serviceFormFillingId);
            setFormVersion(version);
            // respuestas por defecto + previas
            const initial = {};
            form.questionnaire.sections.forEach((sec) => {
                var _a;
                return (_a = sec === null || sec === void 0 ? void 0 : sec.questions) === null || _a === void 0 ? void 0 : _a.forEach((q) => {
                    if ((q === null || q === void 0 ? void 0 : q.defaultValue) !== undefined) {
                        initial[q.variableName] =
                            q.type === "multipleChoice"
                                ? q.defaultValue
                                : String(q.defaultValue);
                    }
                });
            });
            const merged = Object.assign(Object.assign({}, initial), prevAnswers);
            // evaluar derivadas
            const withDerived = Object.assign({}, merged);
            form.questionnaire.sections.forEach((sec) => {
                var _a;
                return (_a = sec === null || sec === void 0 ? void 0 : sec.questions) === null || _a === void 0 ? void 0 : _a.forEach((q) => {
                    const val = withDerived[q === null || q === void 0 ? void 0 : q.variableName];
                    if (val == null)
                        return;
                    Object.assign(withDerived, answerRules.evaluateDerivedAnswers(q, val));
                    if (q.type === "binaryChoice") {
                        Object.assign(withDerived, answerRules.evaluateBinaryDerivedAnswers(q, val, withDerived));
                    }
                });
            });
            setAnswers(withDerived);
            // ACTIVAR PRIMERA SECCIÓN VISIBLE *CON EL CONTEXTO ACTUAL* (aquí sí usamos context)
            // Aviso: si isDpdMandatory cambia en el futuro, NO queremos forzar de nuevo este ID
            const flagId = withDerived['currentSectionFlag'];
            const first = form.questionnaire.sections.find((sec) => !sec.displayConditions ||
                evaluator.evaluateDisplayConditions(sec.displayConditions, withDerived, {
                    activityId: company.activityId,
                    employees: company.employees,
                    isDpdMandatory, // valor actual al montar por primera vez
                }));
            if (!isReadMode && flagId) {
                const flaggedSection = form.questionnaire.sections.find(sec => sec.id === flagId);
                const isFlaggedVisible = !!flaggedSection &&
                    (!flaggedSection.displayConditions ||
                        evaluator.evaluateDisplayConditions(flaggedSection.displayConditions, withDerived, {
                            activityId: company.activityId,
                            employees: company.employees,
                            isDpdMandatory,
                        }));
                if (isFlaggedVisible) {
                    setActiveSectionId(flagId);
                }
                else if (first) {
                    setActiveSectionId(first.id);
                }
            }
            else if (isReadMode) {
                const sections = form.questionnaire.sections || [];
                const ordered = [...sections].sort((a, b) => a.order - b.order);
                const first = ordered[0];
                if (first) {
                    setActiveSectionId(first.id);
                }
            }
            else if (first) {
                setActiveSectionId(first.id);
            }
            if (!isReadMode && company.formStatus === FormStatus.NOT_STARTED) {
                setOpenDialogInitInfo(true);
            }
        }))();
        return () => {
            didCancel = true;
        };
    }, [company.activityId, company.employees]); // <-- Importante: NO incluir `context` aquí
    // ──────────────────────────────────────────────────────────────────────────────
    // El resto de hooks y lógica permanece igual:
    // 2) visibleSections, 3) currentSection, 4) visibleQuestions, 5) allVisibleQuestions,
    // 6) efecto de "borrar respuestas de preguntas que ya no son visibles", etc.
    // ──────────────────────────────────────────────────────────────────────────────
    // 2) Secciones visibles
    const visibleSections = useMemo(() => {
        if (!formState)
            return [];
        return formState.questionnaire.sections.filter((sec) => {
            // Excluir secciones específicas si isReadMode es true
            if (isReadMode && (sec.id === "eipd_management" || sec.id === "documentation_management")) {
                return false;
            }
            // Evaluar condiciones normales
            return (!sec.displayConditions ||
                evaluator.evaluateDisplayConditions(sec.displayConditions, answers, context));
        });
    }, [formState, answers, context, isReadMode]);
    // 3) Sección activa
    const currentSection = useMemo(() => visibleSections.find((s) => s.id === activeSectionId) || null, [visibleSections, activeSectionId]);
    // 4) Preguntas visibles de la sección activa
    const visibleQuestions = useMemo(() => {
        if (!currentSection)
            return [];
        return currentSection === null || currentSection === void 0 ? void 0 : currentSection.questions.filter((q) => !q.displayConditions ||
            evaluator.evaluateDisplayConditions(q === null || q === void 0 ? void 0 : q.displayConditions, answers, context));
    }, [currentSection, answers, evaluator, context]);
    // 5) Todas las preguntas visibles en TODAS las secciones
    const allVisibleQuestions = useMemo(() => {
        if (!formState)
            return [];
        return formState.questionnaire.sections
            .filter((sec) => !sec.displayConditions ||
            evaluator.evaluateDisplayConditions(sec.displayConditions, answers, context))
            .flatMap((sec) => {
            var _a;
            return (_a = sec === null || sec === void 0 ? void 0 : sec.questions) === null || _a === void 0 ? void 0 : _a.filter((q) => !q.displayConditions ||
                evaluator.evaluateDisplayConditions(q.displayConditions, answers, context));
        });
    }, [formState, answers, context, evaluator]);
    // 6) Set con las variableName de todas las preguntas visibles
    const currentlyVisibleVars = useMemo(() => {
        return new Set(allVisibleQuestions.map((q) => q === null || q === void 0 ? void 0 : q.variableName));
    }, [allVisibleQuestions]);
    // 7) Efecto para borrar de `answers` las respuestas a preguntas que dejan de estar visibles
    const prevVisibleRef = useRef(new Set());
    useEffect(() => {
        const prevSet = prevVisibleRef.current;
        const justHidden = [];
        prevSet.forEach((varName) => {
            if (!currentlyVisibleVars.has(varName)) {
                justHidden.push(varName);
            }
        });
        if (justHidden.length > 0) {
            setAnswers((prev) => {
                const copy = Object.assign({}, prev);
                justHidden.forEach((varName) => {
                    delete copy[varName];
                    const detailsKey = `${varName}_details`;
                    if (copy.hasOwnProperty(detailsKey)) {
                        delete copy[detailsKey];
                    }
                });
                return copy;
            });
        }
        prevVisibleRef.current = currentlyVisibleVars;
    }, [currentlyVisibleVars]);
    // 8) Comprobar si todas las preguntas de la sección activa tienen respuesta válida
    const allAnswered = useMemo(() => {
        return visibleQuestions.every((q) => {
            const val = answers[q.variableName];
            if (q.type === "modalForm") {
                if (val !== "1")
                    return val === "0";
                const details = answers[q.variableName + "_details"];
                return Array.isArray(details) ? details.length > 0 : details != null;
            }
            if (q.type === "multipleChoice") {
                return Array.isArray(val) && val.length > 0;
            }
            if (q.type === "binaryChoice") {
                return val === "0" || val === "1";
            }
            if (q.type === "text" || q.type === "numberInput") {
                return typeof val === "string" && val.trim() !== "";
            }
            return false;
        });
    }, [visibleQuestions, answers]);
    // 9) Array de validaciones para la sección activa
    const validations = useMemo(() => {
        if (!currentSection)
            return [];
        // Validaciones “base” de las preguntas visibles
        const baseValidations = currentSection.questions
            .filter((q) => !q.displayConditions ||
            evaluator.evaluateDisplayConditions(q.displayConditions, answers, context))
            .map((q) => {
            var _a;
            return ({
                variableName: q.variableName,
                validation: (_a = q.validation) !== null && _a !== void 0 ? _a : {
                    type: "custom-regex",
                    regex: ".+",
                    message: t("PDDForm.validation.required", "Este campo es obligatorio"),
                },
            });
        });
        // Validaciones extra para modalForm (si la pregunta binaria está en “1”)
        const modalValidations = currentSection.questions
            .filter((q) => q.type === "modalForm" && answers[q.variableName] === "1")
            .map((q) => ({
            variableName: q.variableName + "_details",
            validation: {
                type: "custom-regex",
                regex: ".+",
                message: t("PDDForm.validation.modalMustHaveEntry", "Debes añadir al menos una entrada"),
            },
        }));
        return [...baseValidations, ...modalValidations];
    }, [currentSection, answers, evaluator, context, t]);
    // 10) ¿Sección activa libre de errores?
    const sectionIsValid = checkSectionValid(validations);
    // 11) Handler de cambios de respuestas
    const handleAnswerChange = useCallback((variable, value, question) => {
        var _a;
        if (!question)
            return;
        let newValue = value;
        if (question.type === "multipleChoice") {
            const prev = answers[variable] || [];
            const diff = value.find((v) => !prev.includes(v)) ||
                prev.find((v) => !value.includes(v));
            newValue = processor.processMultiSelectAnswer(question, prev, diff);
        }
        const derived = answerRules.evaluateDerivedAnswers(question, newValue);
        let derivedBinaries = {};
        if (question.type === "binaryChoice") {
            derivedBinaries = answerRules.evaluateBinaryDerivedAnswers(question, newValue, answers);
        }
        setAnswers((prev) => (Object.assign(Object.assign(Object.assign(Object.assign({}, prev), { [variable]: newValue }), derived), derivedBinaries)));
        const rule = (_a = question.validation) !== null && _a !== void 0 ? _a : {
            type: "custom-regex",
            regex: ".+",
            message: "Este campo es obligatorio",
        };
        validateField(variable, newValue, rule);
    }, [answers, processor, answerRules, validateField]);
    // 12) Handler de navegación entre secciones
    const handleSectionNavigate = useCallback((_dummyNextSectionId) => __awaiter(void 0, void 0, void 0, function* () {
        const currentIndex = visibleSections.findIndex(s => s.id === activeSectionId);
        const nextSection = currentIndex >= 0 && currentIndex < visibleSections.length - 1
            ? visibleSections[currentIndex + 1]
            : null;
        let newAnswers = Object.assign({}, answers);
        if (nextSection) {
            if (isReadMode) {
                setActiveSectionId(nextSection.id);
                return;
            }
            const currentSectionBd = newAnswers['currentSectionFlag'];
            if (currentSectionBd) {
                const bdSection = visibleSections.find(s => s.id === currentSectionBd);
                if (!bdSection || nextSection.order > bdSection.order) {
                    newAnswers['currentSectionFlag'] = nextSection.id;
                }
            }
            else {
                newAnswers['currentSectionFlag'] = nextSection.id;
            }
            if ((currentSection === null || currentSection === void 0 ? void 0 : currentSection.navigation.nextSectionId) === "risk_management") {
                const isDpdMandatoryResponse = yield serviceFormFillingService.checkIsDpdMandatory(serviceFormFillingId);
                setIsDpdMandatory(isDpdMandatoryResponse);
            }
            const formResponses = processor.transformToFormResponse(newAnswers);
            yield serviceFormFillingService.updateFormResponses(formResponses, serviceFormFillingId, formVersion);
            setActiveSectionId(nextSection.id);
            yield scrollToTop();
            if (company.formStatus === FormStatus.NOT_STARTED) {
                setFormProgressStatus(FormStatus.IN_PROGRESS);
            }
        }
    }), [answers, formVersion, processor, serviceFormFillingId, visibleSections, activeSectionId]);
    const handleSectionNavigatePrev = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        const currentIndex = visibleSections.findIndex(s => s.id === activeSectionId);
        const prevSection = currentIndex > 0
            ? visibleSections[currentIndex - 1]
            : null;
        if (prevSection) {
            setActiveSectionId(prevSection.id);
            yield scrollToTop();
        }
    }), [visibleSections, activeSectionId]);
    const handleFinishForm = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        const formResponses = processor.transformToFormResponse(answers);
        yield serviceFormFillingService.updateFormResponses(formResponses, serviceFormFillingId, formVersion);
        setOpenDialogFinishForm(true);
        //TODO: Eliminar esto cuando queramos seguir ocn el flujo real, esto es la descarga del json de preguntas
        /* const jsonString = JSON.stringify(formResponses, null, 2);
        const blob = new Blob([jsonString], { type: "application/json" });

        // Creamos un enlace “invisible” que apunte al Blob y disparamos el clic para descargar:
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "formResponses.json";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url); */
    }), [
        answers,
        processor,
        serviceFormFillingService,
        serviceFormFillingId,
        formVersion
        // navigate
    ]);
    const handleModalExitOption = (option) => __awaiter(void 0, void 0, void 0, function* () {
        switch (option) {
            case 1:
                answers['currentSectionFlag'] = currentSection.id;
                const formResponses = processor.transformToFormResponse(answers);
                yield serviceFormFillingService.updateFormResponses(formResponses, serviceFormFillingId, formVersion);
                if (company.formStatus === FormStatus.NOT_STARTED) {
                    setFormProgressStatus(FormStatus.IN_PROGRESS);
                }
                break;
            case 3:
                yield serviceFormFillingRepository.createNewForm(company.companyId, 1, formVersion);
                useCompanyStore.getState().setFormProgressStatus(FormStatus.NOT_STARTED);
                useCompanyStore.getState().setDocumentGenerationStatus(DocumentStatus.NOT_STARTED);
                break;
        }
        navigate('/');
    });
    if (!formState || !currentSection)
        return _jsx(Loader, { fullBlack: true });
    return (_jsxs(_Fragment, { children: [_jsxs("div", Object.assign({ className: "container-xl PDDForm" }, { children: [_jsx(StepperNavigation, { sections: visibleSections, activeSectionId: activeSectionId, translator: translator, cssClass: isReadMode && 'main-template' }), _jsx("form", Object.assign({ className: "row" }, { children: _jsx(SectionRenderer, { isReadMode: isReadMode, section: currentSection, answers: answers, onChange: handleAnswerChange, translator: translator, disabled: false, evaluator: evaluator, context: context }) })), _jsx(SectionNavigator, { isReadMode: isReadMode, handleExitForm: setOpenDialogExitForm, section: currentSection, onNavigate: handleSectionNavigate, onNavigatePrev: handleSectionNavigatePrev, handleFinish: handleFinishForm, isSectionValid: allAnswered && sectionIsValid })] })), _jsx(ModalInitForm, { open: openDialogInitInfo, setOpen: setOpenDialogInitInfo }), _jsx(ModalExitForm, { open: openDialogExitForm, setOpen: setOpenDialogExitForm, onAccept: handleModalExitOption }), _jsx(ModalFinishForm, { isOpen: openDialogFinishForm, setIsOpen: setOpenDialogFinishForm, serviceFormFillingId: serviceFormFillingId })] }));
};
